// The clan cup in the CLAN section of the hub (clan-cup.md §8; mockup
// round 078, cards 06 to 10; R1629).
//
// Four parts, all drawn by this module and mounted by `clan.ts`:
//
//   - card 06, the cup of now on the CLAN CUP page, in its states: the
//     registration with the member picks, the cup day with the clan's
//     place, the team and the check-in, the cancelled cup, and the cup that
//     is over. It draws no card (R1945): alone it is the page body, and
//     beside the CUP RECORD it stands under the cup's name, whose sub-header
//     wears the arcade's amber, so it never reads as a Clan War;
//   - cards 07 and 08, the CUP page: a sub-page of the CLAN CUP section
//     under ‹ CLAN CUP (ui-map-interface.md §16.11), or for a reader in no
//     clan the page in place of the clan screen under ‹ CLAN: THE CUP BRACKET (four columns on a wide pane, the rounds
//     stacked in time order on a narrow one), THE CUP DAY with its line of
//     times and the strip of cup Saturdays, and YOUR NEXT MATCH, three
//     sections of the page, each under its sub-header (R1945);
//   - card 10, the title line under the clan name and the CUP RECORD
//     section.
//
// Every rule is the server's: `GET /api/clan/cup` answers the cup, the team
// and what the caller may press, and `GET /api/cup` the cup bracket. The
// module mirrors no rule; it reads the figures of `[meta.cup]` off the
// answers, so no copy of them lives here. A time in a line is a time the
// clan shares, so it states the local time with the zone mark first and
// UTC second (`sharedClock`, R1752). A cell of the cup bracket and of the
// timeline of the cup day has no room for two times, so it states the
// local time with the zone mark alone (`cellClock`). Every date is the cup
// day in UTC, from
// the words of `[ui.hub_cup]`.

import { button, pillMulti } from '../../choice/choice';
import {
  checkInClanCup, getClanCup, getCupPage, registerClanCup, withdrawClanCup,
  type ClanCup, type Cup, type CupMatch, type CupPage, type CupRules, type CupSide, type MyClan,
} from '../../../net/api';
import { hubClan, hubCup as S } from '../../../gen/strings';
import { assignWithGesture } from '../../nav';
import type { ArmGate } from '../../arm';
import { cupMarkOf, cupName, cupSvg, monthName } from '../../cupmark';
import { HUB_ZONE, TTL_LIVE, formatSpan, secondsUntil } from '../hubstate';
import { isoTime } from '../../../locale/stamp';
import type { RunCall } from './pickup';
import {
  armedButton, armedPress, backBar, empty, esc, failed, figLine, lede, loading, page, ctlRow,
  plain, rows, section, split, type PanelContext, type Row,
} from '../panel';

/** The cache keys of the two reads. */
const CLANCUP_CELL = 'clancup';
const CUPPAGE_CELL = 'cuppage';

/** What the CLAN panel lends the cards: its write path. */
export interface CupHost {
  run: RunCall;
}

/** The cards of the clan cup. */
export interface CupCards {
  /** Card 06, the cup of now: the page body alone, or a section under the
   *  cup's name while the CUP RECORD stands beside it (R1945). */
  card(my: MyClan): string;
  /** Card 10, the title line under the clan name, or ''. */
  titleLine(): string;
  /** Card 10, the CUP RECORD section, or '' while the clan entered no cup. */
  record(): string;
  /** True while the CUP page stands in place of the clan screen. */
  pageOpen(): boolean;
  /** Opens the CUP page with no press: the deep link of a cup post
   *  (`?tab=clan&page=cup`, R1649). */
  show(): void;
  /** Closes the CUP page with no press: the rail of the clan opened
   *  another section. */
  hide(): void;
  /** Cards 07 and 08, the CUP page, under its own ‹ CLAN bar: the page of
   *  a reader in no clan. */
  page(): string;
  /** The CUP page as a sub-page of the CLAN CUP section (§16.16): the
   *  cup's name for the bar the shell draws, and the cards. */
  pageParts(): { name: string; body: string };
  /** The summary of the CLAN CUP section: the cup and the state of the
   *  clan's entry, or '' while the read has not answered. */
  summary(clan: number): string;
  /** Takes one press; answers false for an action that is not a cup's. */
  act(action: string, el: HTMLElement): boolean;
}

// --- the words of a date and a time --------------------------------------

/** "21:00 CEST": a time in a cell of the cup bracket or the cup day, local
 *  with the zone mark (R1752). A cell has no room for the UTC twin. */
export function cellClock(sec: number): string {
  return isoTime(sec, HUB_ZONE);
}

/** "21:00 CEST · 19:00 UTC": a time a clan shares (R1752). The local time
 *  with the zone mark comes first, because the reader acts on it; UTC
 *  comes second, because a clan mate in another zone reads the same. */
export function sharedClock(sec: number): string {
  return S.time_pair({ local: isoTime(sec, HUB_ZONE), utc: isoTime(sec, { utc: true }) });
}

/** "SAT 7 NOV": the day of a unix second, in UTC. */
export function utcDate(sec: number): string {
  const d = new Date(sec * 1000);
  return S.date({ day: S.days_short[d.getUTCDay()], n: d.getUTCDate(), month: S.months_short[d.getUTCMonth()] });
}

/** "NOVEMBER CUP": the cup of a month, "2026-11". */
export function cupOf(month: string): string {
  return S.cup_name({ month: monthName(month) });
}

/** The words of the last three rounds of a cup bracket, the final first. */
const ROUND_NAMES: readonly string[] = [S.round_final, S.round_semi, S.round_quarter];
const ROUND_HEADS: readonly string[] = [S.col_final, S.col_semi, S.col_quarter];

/** The name of round r of a cup bracket of `count` rounds, as one match:
 *  FINAL, SEMI-FINAL, QUARTER-FINAL, then ROUND r. */
function roundName(round: number, count: number): string {
  return ROUND_NAMES[count - round] ?? S.round_n({ n: round });
}

/** The heading of a column of round r: FINAL, SEMI-FINALS, QUARTER-FINALS. */
export function roundHead(round: number, count: number): string {
  return ROUND_HEADS[count - round] ?? S.round_n({ n: round });
}

/** The word of the map pool: "SOVEREIGNTY · 3 AGAINST 3". The category is
 *  the first map's; a pool of several categories names the first. */
export function mapsLine(rules: CupRules): string {
  // The first CORE entry names the category: a community entry of the
  // pool (`community/<CODE>`, clan-cup.md §5.1, R1985) plays in a core
  // category the pool does not state.
  const cat = (rules.maps.find((m) => !m.startsWith('community/')) ?? '').split('/')[0];
  const word = CATEGORY_WORDS[cat] ?? cat.replace(/-/g, ' ').toUpperCase();
  return S.maps_line({ category: word, n: rules.teamSize });
}

const CATEGORY_WORDS: Readonly<Record<string, string>> = {
  sovereignty: S.cat_sovereignty,
  'capture-sov': S.cat_capture_sov,
  ffa: S.cat_ffa,
};

/** The side of a match a clan holds: 1, 2 or 0. */
function sideOf(m: CupMatch, clan: number): number {
  if (clan === 0) return 0;
  if (m.a?.clanId === clan) return 1;
  if (m.b?.clanId === clan) return 2;
  return 0;
}

/** True while a clan holds a side of a match; never for clan 0, which is
 *  no clan. */
function holds(m: CupMatch, clan: number): boolean {
  return sideOf(m, clan) !== 0;
}

/** The next match of the reader's clan on the CUP page: the first match
 *  of the clan that is not done, or undefined. */
function nextOf(cp: CupPage): CupMatch | undefined {
  return cp.matches.find((m) => m.state !== 'done' && holds(m, cp.myClan));
}

/** The round count of a cup: one start per round. */
function roundCount(c: Cup): number {
  return c.rounds.length;
}

/** A pick of one member: plays, reserve, or nothing. */
type Pick = 'player' | 'reserve';

export function createCupCards(ctx: PanelContext, gate: ArmGate, host: CupHost): CupCards {
  /** The members the card has picked, by public id, or null while the
   *  officer has not touched the pick: null draws the registered team. */
  let picks: Map<string, Pick> | null = null;
  let pageOn = false;
  /** The offset of the cup clock from the hub's clock, one per answer: 0 on
   *  a deployment, and the moved clock of a dev process (clan-cup.md §9). */
  const offsets = new WeakMap<object, number>();

  /** Now on the cup clock of one answer. */
  function cupNow(a: { now?: number } | undefined): number {
    const base = ctx.now();
    if (a === undefined || !a.now) return base;
    let off = offsets.get(a);
    if (off === undefined) {
      off = a.now - base;
      offsets.set(a, off);
    }
    return base + off;
  }

  const clanCup = () => ctx.data.cell<ClanCup>(CLANCUP_CELL, TTL_LIVE, getClanCup);
  /** A write of the team went through: the card draws the registered
   *  team again. */
  const settle = (): void => {
    picks = null;
    ctx.data.drop(CLANCUP_CELL);
  };
  const cupPage = () => ctx.data.cell<CupPage>(CUPPAGE_CELL, TTL_LIVE, getCupPage);

  /** The picks the card draws. */
  function picked(cc: ClanCup): Map<string, Pick> {
    if (picks !== null) return picks;
    const out = new Map<string, Pick>();
    for (const m of cc.entry?.members ?? []) out.set(m.id, m.reserve ? 'reserve' : 'player');
    return out;
  }

  /** How many picks of one kind. */
  function count(p: Map<string, Pick>, kind: Pick): number {
    let n = 0;
    for (const v of p.values()) if (v === kind) n++;
    return n;
  }

  /** The cup row of a cup: "NOVEMBER CUP · SAT 7 NOV · 19:00 CET · 18:00 UTC". */
  function whenLine(c: Cup, today = false): string {
    return S.when_line({
      cup: cupOf(c.month), date: today ? S.today : utcDate(c.day), time: sharedClock(c.rounds[0] ?? c.day),
    });
  }

  /** The cancel line of a cancelled cup. */
  function cancelBox(c: Cup, rules: CupRules): string {
    const why = c.cancelReason === 'ops'
      ? S.cancelled_ops
      : S.cancelled_few({ n: c.registered, min: rules.minClans });
    return `<div class="cupcancel"><b>${esc(S.cancelled_head({ cup: cupOf(c.month) }))}</b> ${esc(why)}</div>`;
  }

  /** The place line of the clan on the cup day. `clan` is the clan's id. */
  function placeLine(cc: ClanCup, clan: number): Row {
    const c = cc.cup;
    const e = cc.entry;
    const n = roundCount(c);
    if (e === null) return [S.row_your_clan, S.none];
    if (c.state === 'done' && c.winner?.clanId === clan) return [S.row_your_clan, S.place_winner, 'gold'];
    if (e.outRound > 0) return [S.row_your_clan, S.place_out({ round: roundName(e.outRound, n) })];
    const p = cc.play;
    if (p === null) return [S.row_your_clan, S.place_seed({ seed: e.seed })];
    const m = p.match;
    const them = sideOf(m, clan) === 1 ? m.b : m.a;
    const round = roundName(m.round, n);
    if (them === null) return [S.row_your_clan, S.place_waiting({ round })];
    if (them.clanId === 0) return [S.row_your_clan, S.place_bye({ round })];
    if (m.state === 'live') return [S.row_your_clan, S.place_live({ round, tag: them.tag }), 'good'];
    return [S.row_your_clan, S.place_against({ round, time: sharedClock(c.rounds[m.round - 1] ?? c.day), tag: them.tag }), 'acc'];
  }

  /** The check-in row. */
  function checkinRow(cc: ClanCup): Row {
    const c = cc.cup;
    const e = cc.entry;
    const now = cupNow(cc);
    if (e === null) return [S.row_checkin, S.none];
    if (e.checkedInAt > 0) return [S.row_checkin, S.checkin_done({ time: sharedClock(e.checkedInAt) }), 'good'];
    if (now < c.checkinOpensAt) return [S.row_checkin, S.checkin_opens({ time: sharedClock(c.checkinOpensAt) })];
    if (now < c.checkinClosesAt) return [S.row_checkin, S.checkin_open({ time: sharedClock(c.checkinClosesAt) }), 'gold'];
    return [S.row_checkin, S.checkin_missed, 'bad'];
  }

  /** The member picks of the registration (card 06, open). */
  function pickBlock(my: MyClan, cc: ClanCup): string {
    const p = picked(cc);
    // The picks are several saved values; a reserve is a pick of the
    // second kind, with a dashed rim (R2011).
    const ids = (k: string): string[] => my.members.filter((m) => p.get(m.id) === k).map((m) => m.id);
    const chips = pillMulti(my.members.map((m) => ({
      id: m.id, label: p.get(m.id) === 'reserve' ? `${m.name}${S.reserve_suffix}` : m.name,
    })), ids('player'), 'cup-pick', { alt: ids('reserve'), wrap: true });
    const players = count(p, 'player');
    const reserves = count(p, 'reserve');
    const ready = players === cc.rules.teamSize && reserves <= cc.rules.reservesMax;
    const controls = button(cc.entry === null ? S.register : S.update,
      { act: ready ? 'cup-register' : 'cup-nope' }, { variant: ready ? 'primary' : 'plain' })
      + (cc.entry === null ? '' : armedButton(gate, 'cup-withdraw', S.withdraw, S.withdraw_armed, { act: 'cup-withdraw' }));
    return lede(S.pick_lede({ players: cc.rules.teamSize, reserves: cc.rules.reservesMax }))
      + ctlRow(chips)
      + `<div class="cupfoot"><span class="note2">${esc(S.picked({ players, reserves }))}</span></div>`
      + ctlRow(controls);
  }

  /** The team rows of a registered team. */
  function teamRows(cc: ClanCup): Row[] {
    const e = cc.entry;
    if (e === null) return [];
    const players = e.members.filter((m) => !m.reserve).map((m) => m.name.toUpperCase());
    const reserves = e.members.filter((m) => m.reserve).map((m) => m.name.toUpperCase());
    const out: Row[] = [[S.row_team, players.join(' · ')]];
    if (reserves.length > 0) out.push([S.row_reserve, reserves.join(' · ')]);
    return out;
  }

  /** The buttons of the cup day: the bracket, WATCH, PLAY and CHECK IN. */
  function dayButtons(cc: ClanCup): string {
    let b = button(S.btn_bracket, { act: 'cup-page' }, { variant: 'primary' });
    if (cc.canCheckIn) b += button(S.btn_checkin, { act: 'cup-checkin' }, { variant: 'primary' });
    const p = cc.play;
    if (p?.hostingCode) b += button(S.btn_play, { act: 'cup-play', data: { code: p.hostingCode } }, { variant: 'primary' });
    if (p?.match.replay) b += button(S.btn_watch, { act: 'cup-watch', data: { code: p.match.replay } });
    return ctlRow(b);
  }

  /** The cup of now on the CLAN CUP page (R1945). The page title names
   *  CLAN CUP, so the block draws no card: alone it stands under the page
   *  title, and beside the CUP RECORD it stands under the cup's name with
   *  the state word (REGISTRATION, CUP DAY, CANCELLED, DONE) beside it. */
  function clanCard(my: MyClan): string {
    const cell = clanCup();
    if (cell.state === 'failed') return plain(failed(cell.error), 'cupc');
    const cc = cell.value;
    if (cc === undefined) return plain(loading(), 'cupc');
    const c = cc.cup;
    const now = cupNow(cc);
    // Alone, the block keeps the state word as its first line where the
    // summary of the page does not state it (the cup day of a team).
    const block = (more: string, inner: string): string => (cc.record.length === 0
      ? plain((summary(my.clan.id).includes(more) ? '' : figLine(more)) + inner, 'cupc')
      : section(cupOf(c.month), more, inner, 'cupc'));

    if (c.state === 'cancelled') {
      const next = cc.next;
      const inner = cancelBox(c, cc.rules) + rows([
        [S.row_next, whenLine(next), 'acc'],
        [S.row_reg_opens, utcDate(next.opensAt)],
        [S.row_your_clan, cc.entry === null ? S.none : S.was_registered],
      ]) + `<div class="note2">${esc(S.cancel_note)}</div>`;
      return block(S.more_cancelled, inner);
    }

    if (c.state === 'done') {
      const w = c.winner;
      const list: Row[] = [[S.row_cup, cupOf(c.month), 'acc']];
      if (w !== null) {
        list.push([S.row_winner, S.done_winner({
          tag: w.tag, wins: c.finalWins ?? 0, losses: c.finalLosses ?? 0, runner: c.runnerUpTag ?? '',
        }), 'gold']);
      }
      if (cc.entry !== null) list.push(placeLine(cc, my.clan.id));
      list.push([S.row_next, whenLine(cc.next)]);
      return block(S.more_done, rows(list) + ctlRow(button(S.btn_bracket, { act: 'cup-page' })));
    }

    if (c.state === 'open') {
      const list: Row[] = [[S.row_cup, whenLine(c), 'acc']];
      if (now < c.opensAt) {
        list.push([S.row_reg_opens, utcDate(c.opensAt)]);
      } else {
        const left = secondsUntil(now, c.cutoffAt);
        list.push([S.row_cutoff, left > 0
          ? S.cutoff_line({ date: utcDate(c.cutoffAt), time: sharedClock(c.cutoffAt), left: formatSpan(left) })
          : S.cutoff_past({ date: utcDate(c.cutoffAt), time: sharedClock(c.cutoffAt) })]);
      }
      list.push(
        [S.row_registered, c.registered >= cc.rules.bracketSize
          ? S.registered_full({ n: c.registered, max: cc.rules.bracketSize })
          : S.registered_line({ n: c.registered, min: cc.rules.minClans }),
        c.registered >= cc.rules.minClans ? 'good' : ''],
        [S.row_maps, mapsLine(cc.rules)],
        [S.row_prize, S.prize_value, 'gold'],
      );
      let inner = rows(list);
      if (now < c.opensAt) {
        inner += lede(S.reg_not_open({ date: utcDate(c.opensAt) }));
      } else if (now >= c.cutoffAt) {
        inner += lede(S.cutoff_passed);
      } else if (cc.canRegister) {
        inner += pickBlock(my, cc);
      } else {
        inner += rows(teamRows(cc)) + lede(cc.entry === null ? S.officer_only : S.officer_changes);
      }
      // The CUP page draws the draw as it stands now (card 07), so the way
      // to it stands in the registration too, as a plain button.
      if (c.registered > 0 && now >= c.opensAt) inner += ctlRow(button(S.btn_bracket, { act: 'cup-page' }));
      return block(S.more_registration, inner);
    }

    // drawn or running: the cup day.
    const today = now >= c.day && now < c.day + 86400;
    if (cc.entry === null) {
      const inner = rows([[S.row_cup, whenLine(c, today), 'acc']]) + lede(S.drawn_no_team)
        + ctlRow(button(S.btn_bracket, { act: 'cup-page' }, { variant: 'primary' }));
      return block(S.more_cup_day, inner);
    }
    const inner = rows([[S.row_cup, whenLine(c, today), 'acc'], placeLine(cc, my.clan.id), ...teamRows(cc), checkinRow(cc)])
      + dayButtons(cc);
    return block(S.more_cup_day, inner);
  }

  // --- the CUP page (cards 07 and 08) ------------------------------------

  /** One side of a match box. */
  function sideBox(s: CupSide | null, mine: number, won: boolean, lost: boolean, score: string, round: number): string {
    if (s === null) {
      return `<div class="s"><span class="sd"></span><span class="cn tbd">${esc(S.side_tbd)}</span><span class="sc"></span></div>`;
    }
    if (s.clanId === 0) {
      return `<div class="s bye"><span class="sd"></span><span class="cn">${esc(round === 1 ? S.side_bye : S.side_empty)}</span>`
        + '<span class="sc"></span></div>';
    }
    const cls = `s${won ? ' w' : ''}${lost ? ' l' : ''}${s.clanId === mine ? ' me' : ''}${s.absent ? ' abs' : ''}`;
    return `<div class="${cls}"><span class="sd">${s.seed > 0 ? s.seed : ''}</span>`
      + `<span class="cn">[${esc(s.tag)}] ${esc(s.name)}${s.absent ? ` <i>${esc(S.side_absent)}</i>` : ''}</span>`
      + `<span class="sc">${esc(score)}</span></div>`;
  }

  /** One match box of the cup bracket. */
  function matchBox(m: CupMatch, mine: number, count: number): string {
    const played = m.state === 'done' || m.state === 'live';
    const scored = played && m.outcome !== 'walkover' && m.outcome !== 'empty';
    const scoreA = scored ? String(m.winsA) : '';
    const scoreB = scored ? String(m.winsB) : '';
    const doneA = m.state === 'done' && m.winner === 1;
    const doneB = m.state === 'done' && m.winner === 2;
    const isMine = holds(m, mine);
    const tag = m.state === 'live'
      ? `<span class="tag2 live">${esc(S.live)}</span>`
      : m.round === count && m.bestOf > 1 ? `<span class="tag2 when">${esc(S.best_of({ n: m.bestOf }))}</span>` : '';
    return `<div class="m${m.state === 'live' ? ' live' : ''}${isMine ? ' me' : ''}">${tag}`
      + sideBox(m.a, mine, doneA, m.state === 'done' && !doneA, scoreA, m.round)
      + sideBox(m.b, mine, doneB, m.state === 'done' && !doneB, scoreB, m.round) + '</div>';
  }

  /** The cup bracket: four columns on a wide pane, the rounds stacked in
   *  time order on a narrow one (card 07). */
  function bracket(cp: CupPage): string {
    const c = cp.cup;
    const count = roundCount(c);
    if (c.state === 'cancelled') {
      return `<div class="cupcancel"><b>${esc(S.bracket_cancelled)}</b> `
        + `${esc(S.bracket_cancelled_why({ cup: cupOf(c.month) }))}</div>`;
    }
    if (cp.matches.length === 0) return empty(S.bracket_none);
    const byRound = (r: number) => cp.matches.filter((m) => m.round === r).sort((x, y) => x.slot - y.slot);
    const head = (r: number): string =>
      `<div class="rnd">${esc(S.round_at({ round: roundHead(r, count), time: cellClock(c.rounds[r - 1]) }))}</div>`;
    if (!ctx.wide()) {
      let out = '<div class="brk stack">';
      for (let r = 1; r <= count; r++) {
        out += head(r) + byRound(r).map((m) => matchBox(m, cp.myClan, count)).join('');
      }
      return `${out}</div>`;
    }
    // Each column holds its matches in a box that spreads them over the
    // height of the first round, so a match of a later round stands
    // between the two matches that feed it.
    let out = `<div class="brk cols" style="--rounds:${count}">`;
    for (let r = 1; r <= count; r++) {
      out += `<div class="col">${head(r)}`
        + `<div class="ms">${byRound(r).map((m) => matchBox(m, cp.myClan, count)).join('')}</div></div>`;
    }
    const done = c.state === 'done';
    out += `<div class="col cupcol"><div class="rnd">${esc(S.col_cup)}</div>`
      + `<div class="ms"><div class="bigcup${done ? '' : ' faint'}">${cupSvg(34)}`
      + `<div class="cupyear">${esc(cupName(c.month))}</div></div></div></div>`;
    return `${out}</div>`;
  }

  /** The line of times of the cup day (card 08). */
  function dayLine(cp: CupPage, mineRound: number): string {
    const c = cp.cup;
    const now = cupNow(cp);
    const count = roundCount(c);
    const off = c.state === 'cancelled';
    const ev = (at: number, x: string, y: string, cls: string) =>
      `<div class="ev ${cls}"><span class="tlt">${esc(at > 0 ? cellClock(at) : S.after)}</span>`
      + `<span class="tlx">${esc(x)}</span><span class="tly">${esc(y)}</span></div>`;
    const state = (from: number, to: number) => (now >= to ? 'done' : now >= from ? 'now' : '');
    let out = `<div class="tline${off ? ' off' : ''}">`;
    out += ev(c.checkinOpensAt, S.ev_checkin_opens, S.ev_checkin_opens_y, state(c.checkinOpensAt, c.checkinClosesAt));
    out += ev(c.checkinClosesAt, S.ev_checkin_closes, S.ev_checkin_closes_y, now >= c.checkinClosesAt ? 'done' : '');
    for (let r = 1; r <= count; r++) {
      const from = c.rounds[r - 1];
      const to = r < count ? c.rounds[r] : c.day + 86400;
      const live = cp.matches.some((m) => m.round === r && m.state === 'live');
      const finished = cp.matches.length > 0 && cp.matches.filter((m) => m.round === r).every((m) => m.state === 'done');
      const cls = `${finished ? 'done' : live || state(from, to) === 'now' ? 'now' : ''}${r === mineRound ? ' me' : ''}`;
      const y = r === count ? S.ev_final_y({ n: cp.rules.finalBestOf }) : mapsLine(cp.rules);
      out += ev(from, roundHead(r, count), r === mineRound ? S.ev_mine : y, cls);
    }
    out += ev(0, S.ev_title, S.ev_title_y, c.state === 'done' ? 'done' : '');
    return `${out}</div>`;
  }

  /** The strip of cup Saturdays (card 08). */
  function strip(cp: CupPage): string {
    const cur = cp.cup.month;
    const cells = cp.strip.map((s) => {
      const d = new Date(s.day * 1000);
      let word = '';
      let cls = '';
      if (s.state === 'done') word = s.winnerTag ? S.strip_won({ tag: s.winnerTag }) : S.strip_none;
      else if (s.state === 'cancelled') { word = S.strip_cancelled; cls = 'off'; }
      else if (s.month === cur) { word = cupNow(cp) >= s.day ? S.strip_today : S.strip_next; cls = 'next'; }
      return `<span class="${cls}">${esc(S.months_short[d.getUTCMonth()])}<b>${d.getUTCDate()}</b>${esc(word)}</span>`;
    }).join('');
    return `<div class="kick">${esc(S.strip_head)}</div><div class="cal">${cells}</div>`;
  }

  /** YOUR NEXT MATCH (card 08). */
  function nextMatch(cp: CupPage, cc: ClanCup | undefined): string {
    const c = cp.cup;
    const count = roundCount(c);
    const title = S.next_title;
    if (c.state === 'cancelled') {
      return section(title, '', lede(S.next_cancelled({ cup: cupOf(cc?.next.month ?? c.month), date: utcDate(cc?.next.day ?? c.day) })));
    }
    if (c.state === 'done') return section(title, '', lede(S.next_done({ cup: cupOf(c.month) })));
    // The deep link of a cup post opens the page for a reader in no clan
    // too (R1649); the answer then marks no clan of the reader.
    if (cp.myClan === 0) return section(title, '', lede(S.next_no_clan));
    if (cc === undefined || cc.entry === null) return section(title, '', lede(S.next_none));
    if (cc.entry.outRound > 0) {
      return section(title, '', lede(S.next_out({ time: sharedClock(c.rounds[count - 1]) })));
    }
    const m = nextOf(cp);
    if (m === undefined) return section(title, '', lede(S.next_none));
    const them = m.a?.clanId === cp.myClan ? m.b : m.a;
    const against = them === null ? S.side_tbd : them.clanId === 0 ? S.side_bye : `[${them.tag}] ${them.name.toUpperCase()}`;
    const team = (cc.entry.members ?? []).filter((x) => !x.reserve).map((x) => x.name.toUpperCase()).join(' · ');
    const list = rows([[S.row_round, roundName(m.round, count)], [S.row_against, against], [S.row_team, team]]);
    const when = sharedClock(c.rounds[m.round - 1]);
    if (m.state === 'live') {
      const watch = m.replay ? ctlRow(button(S.btn_watch_short, { act: 'cup-watch', data: { code: m.replay } }, { variant: 'primary' })) : '';
      return section(title, when, `<div class="nownow">${esc(S.next_now)}</div>${list}${watch}`);
    }
    return section(title, when, (cp.preview ? lede(S.next_open_lede) : '') + list);
  }

  /** The CUP page: the cup's name once the page has read it, and the
   *  sections. Both reads start on the first draw: YOUR NEXT MATCH reads the
   *  card's answer for the team. */
  function pageParts(): { name: string; body: string } {
    const cc = clanCup().value;
    const cell = cupPage();
    if (cell.state === 'failed') return { name: '', body: plain(failed(cell.error)) };
    const cp = cell.value;
    if (cp === undefined) return { name: '', body: plain(loading()) };
    const c = cp.cup;
    const clans = c.state === 'open' ? c.registered : c.clans;
    const sub = S.page_sub({ date: utcDate(c.day), clans: S.clans({ n: clans }), maps: mapsLine(cp.rules) });
    const now = cupNow(cp);
    const live = cp.matches.some((m) => m.state === 'live');
    const more = live ? S.bracket_now({ time: sharedClock(now) }) : cp.preview ? S.bracket_preview : '';
    const mineMatch = nextOf(cp);
    const day = section(S.day_title,
      c.state === 'cancelled' ? S.more_cancelled : live ? S.bracket_now({ time: sharedClock(now) }) : S.day_more({ date: utcDate(c.day) }),
      (c.state === 'cancelled' ? `<div class="cupcancel"><b>${esc(S.day_cancelled)}</b></div>` : '')
      + dayLine(cp, mineMatch?.round ?? 0) + strip(cp)
      + `<div class="note2">${esc(S.day_note({ min: cp.rules.noticeBeforeMin }))}</div>`);
    return {
      name: cupOf(c.month),
      // The facts of the cup close the bracket section (R1780).
      body: section(S.bracket_title, more, bracket(cp) + `<div class="note2">${esc(sub)}</div>`, 'cupc')
        + split(ctx.wide(), day, nextMatch(cp, cc)),
    };
  }

  function cupPageHtml(): string {
    // ‹ CLAN at the top left: the one back form of a hub sub-page (R1751).
    // The cup's name stands in grey beside it once the page has read it,
    // and the page draws no head line (R1780).
    const p = pageParts();
    return page(backBar(hubClan.title, 'cup-back', p.name), p.body);
  }

  /** The summary of the CLAN CUP section (ui-map-interface.md §16.11). */
  function summary(clan: number): string {
    const cc = clanCup().value;
    if (cc === undefined) return '';
    const c = cc.cup;
    const line = (state: string): string => S.sum_line({ cup: cupOf(c.month), state });
    if (c.state === 'cancelled') return line(S.more_cancelled);
    if (c.state === 'open') return line(cc.entry === null ? S.more_registration : S.sum_registered);
    if (cc.entry === null) return line(c.state === 'done' ? S.more_done : S.sum_no_team);
    return line(placeLine(cc, clan)[1]);
  }

  // --- card 10 -------------------------------------------------------------

  function titleLine(): string {
    const cc = clanCup().value;
    const t = cc?.title;
    if (!t) return '';
    return `<div class="cupline">${cupMarkOf(t.month, 16)}`
      + `${esc(S.title_line({ cup: cupName(t.month), date: utcDate(t.markUntil ?? 0) }))}</div>`;
  }

  function recordCard(): string {
    const cc = clanCup().value;
    if (cc === undefined || cc.record.length === 0) return '';
    const count = roundCount(cc.cup);
    const list = cc.record.map((r) => {
      const name = S.record_month({ month: monthName(r.month), year: r.month.split('-')[0] });
      if (r.won) {
        return `<div class="row"><span class="k">${esc(name)}</span><span class="v gold">`
          + `${cupMarkOf(r.month, 12)}${esc(S.record_winner({ wins: r.finalWins ?? 0, losses: r.finalLosses ?? 0, tag: r.runnerUpTag ?? '' }))}`
          + '</span></div>';
      }
      let v: string;
      let cls = '';
      if (r.state === 'cancelled') v = S.record_cancelled;
      else if (r.outRound > 0) v = roundName(r.outRound, count);
      else if (r.state === 'open') { v = S.record_registered({ date: utcDate(r.day) }); cls = 'acc'; }
      else if (r.state === 'done') v = S.place_winner;
      else {
        // The cup of now names the round the clan plays, as card 10 draws it.
        const p = r.month === cc.cup.month ? cc.play : null;
        v = p ? S.record_in_round({ round: roundName(p.match.round, count) }) : S.record_in;
        cls = 'good';
      }
      return rows([[name, v, cls]]);
    }).join('');
    return section(S.record_title, S.record_more({ n: cc.titles }),
      list + `<div class="note2">${esc(S.record_note)}</div>`);
  }

  // --- the presses -------------------------------------------------------

  function show(): void {
    pageOn = true;
    ctx.data.drop(CUPPAGE_CELL);
  }

  function act(action: string, el: HTMLElement): boolean {
    switch (action) {
      case 'cup-page':
        show();
        ctx.play('sfx-menu-press');
        ctx.refresh();
        return true;
      case 'cup-back':
        pageOn = false;
        ctx.play('sfx-menu-back');
        ctx.refresh();
        return true;
      case 'cup-pick': {
        const cc = clanCup().value;
        const id = el.dataset.id ?? '';
        if (cc === undefined || id === '') return true;
        const next = new Map(picked(cc));
        const now = next.get(id);
        const players = count(next, 'player');
        const reserves = count(next, 'reserve');
        // A press walks a member from nothing to playing to reserve to
        // nothing; a full row of players skips to reserve, and a full
        // team skips back to nothing.
        if (now === undefined) {
          if (players < cc.rules.teamSize) next.set(id, 'player');
          else if (reserves < cc.rules.reservesMax) next.set(id, 'reserve');
          else { ctx.play('sfx-menu-error'); return true; }
        } else if (now === 'player' && reserves < cc.rules.reservesMax) {
          next.set(id, 'reserve');
        } else {
          next.delete(id);
        }
        picks = next;
        ctx.play('sfx-menu-press');
        ctx.refresh();
        return true;
      }
      case 'cup-nope':
        ctx.play('sfx-menu-error');
        return true;
      case 'cup-register': {
        const cc = clanCup().value;
        if (cc === undefined) return true;
        const p = picked(cc);
        const players = [...p].filter(([, k]) => k === 'player').map(([id]) => id);
        const reserves = [...p].filter(([, k]) => k === 'reserve').map(([id]) => id);
        host.run(S.busy_register, () => registerClanCup(players, reserves), settle);
        return true;
      }
      case 'cup-withdraw':
        if (!armedPress(ctx, gate, 'cup-withdraw')) return true;
        host.run(S.busy_withdraw, () => withdrawClanCup(), settle);
        return true;
      case 'cup-checkin':
        host.run(S.busy_checkin, () => checkInClanCup(), () => { ctx.data.drop(CLANCUP_CELL); });
        return true;
      case 'cup-play': {
        const code = el.dataset.code ?? '';
        if (code !== '') assignWithGesture(`/arcade?host=${encodeURIComponent(code)}&cup=1`);
        return true;
      }
      case 'cup-watch': {
        const code = el.dataset.code ?? '';
        if (code !== '') assignWithGesture(`/replay?r=${encodeURIComponent(code)}`);
        return true;
      }
      default:
        return false;
    }
  }

  return {
    card: clanCard,
    titleLine,
    record: recordCard,
    pageOpen: () => pageOn,
    show,
    hide: () => { pageOn = false; },
    page: cupPageHtml,
    pageParts,
    summary,
    act,
  };
}
